import { ref, onMounted, computed, watch } from 'vue';
import { DateTime } from 'luxon';
import axios from 'axios';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import VueSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import { baseUrl } from '../../env';
import LoadingModal from '@/components/LoadingModal.vue';
import { useRoute } from 'vue-router'; // ✅ import useRoute

export default {
  name: 'MediaPage',

  components: {
    VueDatePicker,
    VueSelect,
    LoadingModal
  },

  setup() {
    const route = useRoute();
    const selectedMedia = ref([]);
    const mediaKeyword = ref('');
    const date = ref({ from: null, to: null });
    const media = ref([]);
    const data = ref([]);
    const fundsSum = ref(0);
    const loading = ref(false);
    const loadingLabel = ref('Учитавање резултата...');
    const sortKey = ref('date');
    const sortOrder = ref('desc');
    const submitPerformed = ref(false);

    const fetchMedia = async () => {
      try {
        const response = await axios.get(`${baseUrl}/media`);
        response.data.unshift({
          media_name: 'Svi mediji',
          id: 'all',
        });
        media.value = response.data;

        // ✅ Preselect media based on query
        const queryMediaId = route.query.mediaid;
        if (queryMediaId) {
          const found = media.value.find(m => String(m.id) === String(queryMediaId));
          if (found) selectedMedia.value = [found];
        }

      } catch (error) {
        console.error('Error fetching media:', error);
      }
    };

    // Watch keyword and update selectedMedia
    watch(mediaKeyword, (keyword) => {
      const lower = keyword.toLowerCase().trim();
      if (!lower) {
        selectedMedia.value = [];
        return;
      }
      const matches = media.value.filter(m =>
        m.media_name.toLowerCase().includes(lower)
      );
      selectedMedia.value = matches;
    });

    const fetchData = async () => {
      submitPerformed.value = true;
      loading.value = true;
      try {
        const from = date.value.from
          ? DateTime.fromJSDate(date.value.from).toFormat('dd.MM.yyyy.')
          : '01.01.1971.';
        const to = date.value.to
          ? DateTime.fromJSDate(date.value.to).toFormat('dd.MM.yyyy.')
          : DateTime.local().toFormat('dd.MM.yyyy.');

        const mediaIds = Array.isArray(selectedMedia.value)
          ? selectedMedia.value.map(item => item.id).join(',')
          : '';

        let response;
        if (mediaIds) {
          response = await axios.get(`${baseUrl}/projects/${mediaIds}/${from}/${to}`);
        } else {
          response = await axios.get(`${baseUrl}/projects/all/${from}/${to}`);
        }

        data.value = response.data.map((project) => ({
          ...project,
          commissionMembers: project.commissionMembers || [],
        }));

        fundsSum.value = data.value
          .reduce((sum, project) => sum + project.funds, 0)
          .toLocaleString('sr');
      } catch (error) {
        console.error('Error fetching data:', error);
      }
      finally {
        loading.value = false;
      }
    };

    function parseDate(str) {
      if (!str) return new Date(0);
      const [day, month, year] = str.split('.');
      return new Date(`${year}-${month}-${day}`);
    }

    const sortedData = computed(() => {
      if (!sortKey.value) return data.value;

      return [...data.value].sort((a, b) => {
        let valA, valB;
        if (sortKey.value === 'submitterName') {
          valA = a.submitter?.name ?? '';
          valB = b.submitter?.name ?? '';
        } else {
          valA = a[sortKey.value] ?? '';
          valB = b[sortKey.value] ?? '';
        }
        if (sortKey.value === 'date') {
          const dateA = parseDate(valA);
          const dateB = parseDate(valB);
          return sortOrder.value === 'asc' ? dateA - dateB : dateB - dateA;
        }
        if (typeof valA === 'number' && typeof valB === 'number') {
          return sortOrder.value === 'asc' ? valA - valB : valB - valA;
        }
        return sortOrder.value === 'asc'
          ? String(valA).localeCompare(String(valB))
          : String(valB).localeCompare(String(valA));
      });
    });

    function sortBy(key) {
      if (sortKey.value === key) {
        sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
      } else {
        sortKey.value = key;
        sortOrder.value = 'asc';
      }
    }

    const submit = () => {
      fetchData();
    };

    onMounted(() => {
      fetchMedia();
    });

    return {
      selectedMedia,
      mediaKeyword,
      date,
      media,
      data,
      fundsSum,
      submit,
      sortKey,
      sortOrder,
      sortedData,
      sortBy,
      loadingLabel,
      loading
    };
  },
};
